// assets/storefront-client.js
// Minimal Storefront GraphQL client with 2-minute caching + in-flight de-dupe.
// Environment variables are injected during build process.

(() => {
    const CFG = () => ({
        ...window.STOREFRONT_CONFIG
    });
    const TTL_DEFAULT = 2 * 60 * 1000; // 2 minutes

    // --- tiny helpers ---
    const sleep = (ms) => new Promise(r => setTimeout(r, ms));
    const hash32 = (str) => {
        let h = 2166136261 >>> 0;
        for (let i = 0; i < str.length; i++) {
            h ^= str.charCodeAt(i);
            h = Math.imul(h, 16777619);
        }
        return (h >>> 0).toString(36);
    };

    // --- cache store (memory + sessionStorage) ---
    const mem = new Map();
    const ss = (() => { try { return window.sessionStorage; } catch { return null; } })();
    const PREFIX = 'sfgql:';

    const now = () => Date.now();
    const read = (key) => {
        if (mem.has(key)) return mem.get(key);
        if (!ss) return null;
        const raw = ss.getItem(PREFIX + key);
        if (!raw) return null;
        try {
            const v = JSON.parse(raw);
            mem.set(key, v);
            return v;
        } catch { return null; }
    };
    const write = (key, value) => {
        mem.set(key, value);
        if (ss) ss.setItem(PREFIX + key, JSON.stringify(value));
    };
    const remove = (key) => {
        mem.delete(key);
        if (ss) ss.removeItem(PREFIX + key);
    };
    const clear = () => {
        mem.clear();
        if (!ss) return;
        const toDel = [];
        for (let i = 0; i < ss.length; i++) {
            const k = ss.key(i);
            if (k && k.startsWith(PREFIX)) toDel.push(k);
        }
        toDel.forEach(k => ss.removeItem(k));
    };

    // --- base fetch (no cache) ---
    async function rawStorefrontQuery(query, variables = {}, init = {}) {
        const { domain, token } = CFG();
        if (!domain || !token || domain.includes('__') || token.includes('__')) {
            throw new Error('[Storefront] Missing or invalid domain/token configuration.');
        }

        const endpoint = `https://${domain}/api/2024-07/graphql.json`;
        const headers = Object.assign(
            {
                'X-Shopify-Storefront-Access-Token': token,
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            init.headers || {}
        );

        const retries = Number.isFinite(init.retries) ? init.retries : 2;
        const baseDelay = Number.isFinite(init.baseDelay) ? init.baseDelay : 300;

        let attempt = 0;
        while (true) {
            const res = await fetch(endpoint, {
                method: 'POST',
                headers,
                body: JSON.stringify({ query, variables }),
                ...init,
                headers // ensure our headers win
            });

            const text = await res.text();
            let payload = {};
            try { payload = text ? JSON.parse(text) : {}; } catch {}
            const ok = res.ok && !payload.errors;

            if (ok) return payload.data;

            const status = res.status;
            const messages = payload.errors?.map(e => e.message).join('; ');
            const err = new Error(messages || `Storefront request failed (HTTP ${status})`);
            err.status = status;
            err.payload = payload;

            if ((status === 429 || status >= 500) && attempt < retries) {
                const retryAfter = parseInt(res.headers.get('retry-after') || '0', 10);
                const delay = retryAfter ? retryAfter * 1000 : baseDelay * Math.pow(2, attempt);
                attempt++;
                await sleep(delay);
                continue;
            }
            throw err;
        }
    }

    // --- cache wrapper ---
    const inflight = new Map();

    /**
     * window.makeStorefrontQuery(query, variables?, init?)
     * Caching options in init:
     *  - cacheTTL: ms (default 120_000)
     *  - swr: boolean (default true) — return stale immediately & refresh in background
     *  - noCache: boolean (default false)
     *  - cacheKey: string (override)
     */
    async function makeStorefrontQuery(query, variables = {}, init = {}) {
        const isMutation = /^\s*mutation\b/i.test(query);
        const cacheable = !isMutation && !init.noCache;
        const ttl = Number.isFinite(init.cacheTTL) ? init.cacheTTL : TTL_DEFAULT;
        const swr = init.swr !== false; // default true

        const key = init.cacheKey || `gql:${hash32(query)}:${hash32(JSON.stringify(variables || {}))}`;

        // fresh hit
        if (cacheable) {
            const rec = read(key);
            if (rec && rec.expires > now()) return rec.value;

            // stale-while-revalidate
            if (rec && swr) {
                if (!inflight.has(key)) {
                    const p = rawStorefrontQuery(query, variables, init)
                        .then(d => { write(key, { value: d, expires: now() + ttl }); return d; })
                        .finally(() => inflight.delete(key));
                    inflight.set(key, p);
                }
                return rec.value; // return stale
            }

            // de-dupe
            if (inflight.has(key)) return inflight.get(key);
        }

        const promise = rawStorefrontQuery(query, variables, init)
            .then(d => {
                if (cacheable) write(key, { value: d, expires: now() + ttl });
                return d;
            })
            .finally(() => inflight.delete(key));

        if (cacheable) inflight.set(key, promise);
        return promise;
    }

    // expose
    window.makeStorefrontQuery = makeStorefrontQuery;
    window.makeStorefrontQuery.cache = {
        clear,
        delete: remove,
        key: (q, v) => `gql:${hash32(q)}:${hash32(JSON.stringify(v || {}))}`,
        _TTL_DEFAULT: TTL_DEFAULT
    };
})();
